import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronRight, FolderPlus, PackagePlus, Pencil, Trash2, Package, FolderX } from "lucide-react";
import { CatalogIcon } from "@/lib/catalogIcons";
import { fmtNum } from "@/lib/helpers";
import { fmtTL } from "@/components/RevenueDialog";

function ProductRow({ p, depth, a }) {
  return (
    <div className="flex items-start gap-3 py-2 pr-2 rounded-lg hover:bg-slate-800/30 transition-colors" style={{ paddingLeft: depth * 24 + 12 }}
         data-testid={`product-row-${p.sku}`}>
      <Checkbox className="mt-1" checked={a.selected.has(p.id)} onCheckedChange={() => a.toggleSelect(p.id)} data-testid={`product-select-${p.sku}`} />
      <span className="h-8 w-8 rounded-lg bg-blue-500/10 border border-blue-500/25 flex items-center justify-center shrink-0"><Package size={15} className="text-blue-300" /></span>
      <div className="min-w-0 flex-1">
        <div className={`font-semibold text-sm break-words leading-snug ${p.is_active ? "" : "text-slate-500 line-through"}`}>{p.name}</div>
        <div className="font-mono text-[11px] text-slate-500">{p.sku}</div>
      </div>
      <span className="text-sm font-bold text-amber-300 tabular-nums whitespace-nowrap mt-0.5">{fmtNum(p.points)}p</span>
      {a.showRev && (
        <button type="button" disabled={!a.admin} onClick={() => a.setRevenueFor(p)} data-testid={`product-revenue-${p.sku}`}
                className={`px-2 py-0.5 rounded-md text-xs font-bold whitespace-nowrap hover:bg-emerald-500/10 ${p.avg_revenue != null ? "text-emerald-300" : "text-slate-500"}`}>
          {p.avg_revenue != null ? fmtTL(p.avg_revenue) : a.admin ? "+ Gelir" : "—"}
        </button>
      )}
      <Badge className={p.is_active ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-slate-700 text-slate-400"}>{p.is_active ? "Aktif" : "Pasif"}</Badge>
      <div className="flex whitespace-nowrap -mt-1">
        <Button size="sm" variant="ghost" onClick={() => a.openEdit(p)} data-testid={`product-edit-${p.sku}`}><Pencil size={14} /></Button>
        <Button size="sm" variant="ghost" onClick={() => a.remove(p)} data-testid={`product-delete-${p.sku}`}><Trash2 size={14} className="text-red-400" /></Button>
      </div>
    </div>
  );
}

function CatRow({ c, depth, tree, a }) {
  const kids = tree.children[c.id] || [];
  const open = a.isOpen(c.id);
  return (
    <div className="group flex items-center gap-2 py-2 pr-2 rounded-xl hover:bg-slate-800/40 transition-colors" style={{ paddingLeft: depth * 24 + 4 }}
         data-testid={`cat-node-${c.id}`}>
      <button onClick={() => a.toggle(c.id)} className="flex items-center gap-2.5 flex-1 min-w-0 text-left" data-testid={`cat-toggle-${c.id}`}>
        <ChevronRight size={15} className={`text-slate-500 transition-transform shrink-0 ${open ? "rotate-90" : ""}`} />
        <span className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${c.color || "#3b82f6"}26`, border: `1px solid ${c.color || "#3b82f6"}66` }}>
          <CatalogIcon name={c.icon} size={17} />
        </span>
        <span className={`font-semibold break-words leading-snug ${depth === 0 ? "font-display text-base" : "text-sm"}`}>{c.name}</span>
        <span className="text-[11px] text-slate-500 whitespace-nowrap" data-testid={`cat-count-${c.id}`}>{tree.count(c.id)} ürün{kids.length ? ` · ${kids.length} alt` : ""}</span>
      </button>
      <div className="flex opacity-70 group-hover:opacity-100 whitespace-nowrap">
        <Button size="sm" variant="ghost" title="Alt kategori ekle" onClick={() => a.newCat(c.id)} data-testid={`cat-add-sub-${c.id}`}><FolderPlus size={14} className="text-blue-300" /></Button>
        <Button size="sm" variant="ghost" title="Bu kategoriye ürün ekle" onClick={() => a.newProduct(c.id)} data-testid={`cat-add-product-${c.id}`}><PackagePlus size={14} className="text-emerald-300" /></Button>
        <Button size="sm" variant="ghost" title="Düzenle" onClick={() => a.editCat(c)} data-testid={`cat-edit-${c.id}`}><Pencil size={14} /></Button>
        <Button size="sm" variant="ghost" title="Sil" onClick={() => a.removeCat(c)} data-testid={`cat-delete-${c.id}`}><Trash2 size={14} className="text-red-400" /></Button>
      </div>
    </div>
  );
}

function flatten(tree, isOpen, filtering) {
  const rows = [];
  const stack = [...(tree.children._root || [])].reverse().map((c) => ({ c, depth: 0 }));
  while (stack.length) {
    const it = stack.pop();
    if (it.p) { rows.push({ type: "prod", p: it.p, depth: it.depth }); continue; }
    const { c, depth } = it;
    if (filtering && tree.count(c.id) === 0) continue;
    rows.push({ type: "cat", c, depth });
    if (!isOpen(c.id)) continue;
    const kids = tree.children[c.id] || [], prods = tree.products[c.id] || [];
    if (!kids.length && !prods.length) rows.push({ type: "empty", id: c.id, depth: depth + 1 });
    for (let i = prods.length - 1; i >= 0; i--) stack.push({ p: prods[i], depth: depth + 1 });
    for (let i = kids.length - 1; i >= 0; i--) stack.push({ c: kids[i], depth: depth + 1 });
  }
  return rows;
}

export function ProductTree({ catalogs, products, a }) {
  const [openMap, setOpenMap] = useState({});
  const tree = useMemo(() => {
    const ids = new Set(catalogs.map((c) => c.id));
    const children = {}, prodsBy = {};
    catalogs.forEach((c) => { const k = c.parent_id && ids.has(c.parent_id) ? c.parent_id : "_root"; (children[k] ||= []).push(c); });
    products.forEach((p) => { const k = p.catalog_id && ids.has(p.catalog_id) ? p.catalog_id : "_none"; (prodsBy[k] ||= []).push(p); });
    const memo = {};
    const count = (id) => memo[id] ?? (memo[id] = (prodsBy[id]?.length || 0) + (children[id] || []).reduce((s, k) => s + count(k.id), 0));
    return { children, products: prodsBy, count };
  }, [catalogs, products]);
  const api = { ...a, isOpen: (id) => a.filtering || !!openMap[id], toggle: (id) => setOpenMap((o) => ({ ...o, [id]: !o[id] })) };
  const roots = tree.children._root || [];
  const orphans = tree.products._none || [];
  const setAll = (v) => setOpenMap(v ? Object.fromEntries(catalogs.map((c) => [c.id, true]).concat([["_none", true]])) : {});
  return (
    <Card className="border-slate-800 bg-slate-900/60 glass p-3" data-testid="product-tree">
      <div className="flex justify-end gap-2 mb-2">
        <Button size="sm" variant="ghost" onClick={() => setAll(true)} data-testid="product-tree-expand">Tümünü Aç</Button>
        <Button size="sm" variant="ghost" onClick={() => setAll(false)} data-testid="product-tree-collapse">Kapat</Button>
      </div>
      {flatten(tree, api.isOpen, a.filtering).map((r) => r.type === "cat" ? <CatRow key={r.c.id} c={r.c} depth={r.depth} tree={tree} a={api} />
        : r.type === "prod" ? <ProductRow key={r.p.id} p={r.p} depth={r.depth} a={api} />
        : <div key={`e-${r.id}`} className="text-xs text-slate-500 py-2" style={{ paddingLeft: r.depth * 24 + 28 }}>Boş kategori — ürün veya alt kategori ekleyin</div>)}
      {orphans.length > 0 && (
        <div className="mt-2 border-t border-slate-800 pt-2" data-testid="cat-node-none">
          <button onClick={() => api.toggle("_none")} className="flex items-center gap-2.5 py-2 px-1 text-left w-full" data-testid="cat-toggle-none">
            <ChevronRight size={15} className={`text-slate-500 transition-transform ${api.isOpen("_none") ? "rotate-90" : ""}`} />
            <FolderX size={17} className="text-amber-400" /><span className="font-semibold text-sm">Kategorisi olmayan ürünler</span>
            <span className="text-[11px] text-amber-400">{orphans.length} ürün · satış ekranında görünmez</span>
          </button>
          {api.isOpen("_none") && orphans.map((p) => <ProductRow key={p.id} p={p} depth={0} a={api} />)}
        </div>
      )}
      {!roots.length && !orphans.length && <div className="text-center text-slate-500 py-12 text-sm">Henüz kategori yok — “Yeni Kategori” ile başlayın</div>}
    </Card>
  );
}
